import React, { useEffect, useRef, useState } from 'react';
import Overlay, { StopPropagation } from '@ynap/overlay';
import { useProductDetails } from '@ynap/api-hooks';
import BEMHelper from '@ynap/bem-helper';
import Spinner from '@ynap/spinner';
import { SizeSelect } from '@ynap/product-details-page';

const bem = new BEMHelper('SizeSelectModal');

type SizeSelectModalProps = {
    product: any;
    wishlist: any;
    messages: any;
    optimizely: any;
    onAddToWishlist: (sizeSku: any, closeModal: () => void) => Promise<any>;
    onCloseModal: () => void;
    onPendingItemCloseModal: () => void;
};

type ProductColor = { sKUs: any[]; price: any };

const SizeSelectModal = ({ product, wishlist, messages, optimizely, onAddToWishlist, onCloseModal, onPendingItemCloseModal }: SizeSelectModalProps) => {
    const [productColor, setProductColor] = useState<ProductColor>({
        sKUs: [],
        price: {},
    });
    const sizeSelectRef = useRef(null);
    const { isProductDetailsLoading, details: productDetails } = useProductDetails({
        partNumber: product.partNumber,
    });

    const { sKUs, price } = productColor;

    // Duplicate sku filtering https://git.yoox.net/projects/yos/repos/yos/browse/packages/state/product-details-state/src/selectors/getSelectedProduct.js?at=3f21483c4e44ef2bee81bf6b73f54115b574c1b9#350
    const displayableSkus = sKUs.filter((sku) => sku.displayable && !sku.banned);
    const hasMeaningfulSize = !['unsized', 'n/a', 'one size', 'onesize', 'yoox con'].includes((String(productDetails?.centralSizeScheme) || '').toLowerCase());

    const changeSelectedSku = (sizeSku: any) => {
        optimizely?.track('plp_pdp_atw');
        onAddToWishlist(sizeSku, onCloseModal);
    };

    const isLoading = isProductDetailsLoading || !!wishlist?.isWishListLoading;

    useEffect(() => {
        if (!isProductDetailsLoading && productDetails?.productColours) {
            const productColorDetails = productDetails.productColours[0];
            const { sKUs, price } = productColorDetails;
            setProductColor({ sKUs, price });
        }
    }, [productDetails]);

    useEffect(() => {
        if (displayableSkus.length === 1 && !hasMeaningfulSize) {
            optimizely?.track('plp_pdp_atw');
            onAddToWishlist(displayableSkus[0], onCloseModal);
        }
    }, [displayableSkus.length]);

    if (!displayableSkus.length || (displayableSkus.length === 1 && !hasMeaningfulSize)) return null;

    return (
        <StopPropagation>
            <Overlay
                type="success"
                title={messages.sizes.initialValue()}
                handleBodyClick={onPendingItemCloseModal}
                onClose={onPendingItemCloseModal}
                borderStyle="borderStyleShort"
                className={{ container: bem('modal'), title: bem('overlayTitle') }}
            >
                <Spinner loading={isLoading}>
                    <SizeSelect
                        alwaysGrid
                        disableNotification
                        disableOneSizeLeft
                        displayLabel={false}
                        hasMeaningfulSize={hasMeaningfulSize}
                        messages={messages}
                        price={price}
                        sizeGridFeatureToggle
                        sizeSelectRef={sizeSelectRef}
                        skus={displayableSkus}
                        changeSelectedSku={changeSelectedSku}
                        className={bem('sizeSelect')}
                        headingClassName={bem('sizeSelectHeading')}
                    />
                </Spinner>
            </Overlay>
        </StopPropagation>
    );
};

export default SizeSelectModal;
